import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import ConstantInput from 'components/sales/Input/constantInput/constantInput';
import AdvancePISection from '../AdvancePISection';
import {
    InvoiceCountSelector,
    SectionHeader,
    InvoicePreviewButton,
    SingleTimeCheckboxes,
} from './index';
import {
    SINGLE_LINE_ITEMS,
    HARDWARE_NOMENCLATURE_LINES,
    SINGLE_INVOICE_LINES,
    SOLUTION_BOQ_ITEMS,
    SINGLE_LINE_SOL_ITEMS,
    NOMENCLATURE_LINES,
    CONSUMABLE_BOQ_ITEMS,
    CONSUMABLE_LINE_ITEMS,
    CONSUMABLE_NOMENCLATURE_LINES,
    CONSUMABLES_FREQUENCY,
} from '../constants/invoiceConstants';

const UnifiedInvoiceModule = ({
    setInputData,
    inputData,
    handleCheckboxChange,
    handleDescriptionChange,
    lineData,
    isCheckboxDisabled,
    handleResetLineItems,
    handleInvoicePreview,
    moduleType = 'single', // 'single', 'solution', 'consumable'
}) => {
    /** ------------------------------
     * Handlers
     * ------------------------------ */
    const confirmResetAndSet = (updates) => {
        if (window.confirm('This action will reset your existing data. Are you sure?')) {
            setInputData({ ...inputData, ...updates });
            handleResetLineItems(3030);
        }
    };

    /** ------------------------------
     * Renderers
     * ------------------------------ */
    const renderInvoiceCountSelector = () => {
        if (moduleType === 'single') {
            return (
                <InvoiceCountSelector
                    invoiceCount={inputData?.invoice_count}
                    onInvoiceCountChange={(newCount) =>
                        setInputData({ ...inputData, invoice_count: newCount })
                    }
                    onResetLineItems={handleResetLineItems}
                    resetLineItemId={3030}
                    invoiceCountOptions={SINGLE_INVOICE_LINES}
                />
            );
        }

        const maxInvoices = moduleType === 'consumable' ? 5 : 4;
        return (
            <div className="col-md-6 d-inline-flex align-items-center">
                <span className="me-2"># Invoices?</span>
                <select
                    style={{ width: '45px', height: '26px' }}
                    value={inputData?.invoice_count}
                    onChange={(e) =>
                        confirmResetAndSet({ invoice_count: parseInt(e.target.value, 10) })
                    }
                >
                    {Array.from({ length: maxInvoices }, (_, i) => i + 1).map((num) => (
                        <option key={num} value={num}>
                            {num}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    const renderConsumablesDropdown = () => {
        if (moduleType === 'single') return null;
        if (moduleType === 'solution' && !inputData.installation_amount) return null;

        return (
            <div className="col-md-6">
                <div className="flex form-group">
                    <label
                        htmlFor="Consumables-frequency"
                        className="whitespace-nowrap font-medium"
                    >
                        Consumables Nomenclature?
                    </label>
                    <select
                        id="Consumables-frequency"
                        className="border border-gray-300 rounded px-3 py-2 w-full max-w-sm form-control"
                        value={inputData.invoice_consumable_boq_value}
                        onChange={(e) => {
                            const selected = CONSUMABLES_FREQUENCY.find(
                                (opt) => opt.name === e.target.value
                            );
                            confirmResetAndSet({
                                invoice_consumable_boq_id: selected?.id || '',
                                invoice_consumable_boq_value: e.target.value,
                                invoice_count: 1,
                            });
                        }}
                        required
                    >
                        <option value="" disabled>
                            Select an option
                        </option>
                        {CONSUMABLES_FREQUENCY.map((option) => (
                            <option key={option.id} value={option.name}>
                                {option.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    };

    const renderInvoicePreviewButton = () => (
        <div className='row'>
            <div className={`col-md-12 text-right`}>
                <button onClick={handleInvoicePreview} className="btn btn-theme mb-2 btn-sm">
                    Invoice Preview
                </button>
            </div>
        </div>
        
    );

    const renderRow = ({
        invoiceIndex,
        rowIndex,
        label,
        itemsList,
        showCheckbox = true,
        offset = 0,
        showBorder = false,
        blankTd = false,
        lineText = ''
    }) => (
        <tr key={rowIndex}>
             <td style={{ ...(showBorder && rowIndex === 0 ? { borderTop: '1px solid #212529', backgroundColor: '#f0f0f0' } : { backgroundColor: '#f0f0f0' }) }}>
                {showBorder && rowIndex === 0 ? `Invoice # ${invoiceIndex + 1}: ` : ''}
            </td>
            <td style={{ ...(showBorder && rowIndex === 0 ? { borderTop: '1px solid #212529', backgroundColor: '#f0f0f0' } : { backgroundColor: '#f0f0f0' }) }}>
                {lineText ? lineText : `Line ${rowIndex + 1 + offset}:`}
            </td>
            {showCheckbox && (
                <SingleTimeCheckboxes
                    inputData={inputData}
                    handleCheckboxChange={handleCheckboxChange}
                    lineData={lineData}
                    isCheckboxDisabled={isCheckboxDisabled}
                    itemsList={itemsList}
                    invoiceIndex={invoiceIndex}
                    rowIndex={rowIndex + offset}
                    showBorder={showBorder}
                />
            )}
            {blankTd && (
                <td style={{ ...(showBorder && rowIndex === 0 ? { borderTop: '1px solid #212529', backgroundColor: '#f0f0f0' } : { backgroundColor: '#f0f0f0' }) }}></td>
            )}
            <td style={{ ...(showBorder && rowIndex === 0 ? { borderTop: '1px solid #212529', backgroundColor: '#f0f0f0' } : { backgroundColor: '#f0f0f0' }) }}>
                {label && (
                    <ConstantInput
                        label={`Description`}
                        type="text"
                        value={lineData[invoiceIndex]?.[rowIndex + offset]?.description || ''}
                        handleChange={(val) =>
                            handleDescriptionChange(invoiceIndex, rowIndex + offset, val)
                        }
                    />
                )}
            </td>
        </tr>
    );

    const renderTableContent = (invoiceIndex) => {
        console.log(moduleType,'moduleType');
        switch (moduleType) {
            case 'single':
                return (
                    <tbody>
                        {HARDWARE_NOMENCLATURE_LINES.map((label, rowIndex) =>
                            renderRow({
                                invoiceIndex,
                                rowIndex,
                                label,
                                itemsList: SINGLE_LINE_ITEMS,
                                showBorder: true,
                            })
                        )}
                    </tbody>
                );

            case 'solution':
                return (
                    <tbody>
                        {renderRow({
                            invoiceIndex,
                            rowIndex: 0,
                            label: null,
                            itemsList: SOLUTION_BOQ_ITEMS,
                            showBorder: true,
                            lineText: 'Line (1-n):'
                        })}
                        {NOMENCLATURE_LINES.map((label, rowIndex) =>
                            renderRow({
                                invoiceIndex,
                                rowIndex,
                                label,
                                itemsList: SINGLE_LINE_SOL_ITEMS,
                                offset: 1,
                            })
                        )}
                    </tbody>
                );

            case 'consumable':
                return (
                    <tbody>
                        {renderRow({
                            invoiceIndex,
                            rowIndex: 0,
                            label: null,
                            itemsList: CONSUMABLE_BOQ_ITEMS,
                            showBorder: true,
                            lineText: 'Line (1-n):'
                        })}
                        {CONSUMABLE_NOMENCLATURE_LINES.map((label, rowIndex) =>
                            renderRow({
                                invoiceIndex,
                                rowIndex,
                                label,
                                itemsList: CONSUMABLE_LINE_ITEMS,
                                offset: 1,
                                blankTd: true,
                            })
                        )}
                    </tbody>
                );

            default:
                return null;
        }
    };

    /** ------------------------------
     * Component JSX
     * ------------------------------ */
    return (
        <>
            <SectionHeader title="Invoicing Format ?" />
            <div className="row">
                {renderInvoiceCountSelector()}
                {renderConsumablesDropdown()}
            </div>

            <br />
            <br />

            <div className="row">
                <div className="col-md-12">
                    {inputData.invoice_count > 0 &&
                        [...Array(parseInt(inputData.invoice_count, 10))].map((_, invoiceIndex) => (
                            <Fragment key={invoiceIndex}>
                                <table className="table" style={{ border: '1px solid #212529' }}>
                                    {renderTableContent(invoiceIndex)}
                                </table>
                            </Fragment>
                        ))}
                </div>
            </div>

            {renderInvoicePreviewButton()}

            <div className="row">
                <div className="col-md-12">
                    <hr />
                </div>
            </div>

            <AdvancePISection inputData={inputData} setInputData={setInputData} />
        </>
    );
};

UnifiedInvoiceModule.propTypes = {
    setInputData: PropTypes.func.isRequired,
    inputData: PropTypes.object.isRequired,
    handleCheckboxChange: PropTypes.func.isRequired,
    handleDescriptionChange: PropTypes.func.isRequired,
    lineData: PropTypes.object.isRequired,
    isCheckboxDisabled: PropTypes.func.isRequired,
    handleResetLineItems: PropTypes.func.isRequired,
    handleInvoicePreview: PropTypes.func.isRequired,
    moduleType: PropTypes.oneOf(['single', 'solution', 'consumable']).isRequired,
};

export default UnifiedInvoiceModule;
